// Organisations (spec/14 §3.1, F-HQ-02): every organisation on the platform, newest first, a page at a
// time (`GET /v1/hq/organizations`), and the way to create one. It is HQ's front page, so it opens with
// a strip of figures over what is loaded, then the list — as a table, or as the franchise tree
// (ADR-0019: franchisors with their franchisees under them, independents on their own).
//
// The api pages the list and takes no search, so the search here narrows what is loaded. Its columns
// are the record's — name, legal name, kind, status, HQ access, created — the owner's: joined, or
// invited, expired or withdrawn (ADR-0046), and where each stands in its setup (spec/14 §3.2, F-HQ-03;
// `SetupProgress.tsx`). "Owner not joined yet" asks the api for only the cafés still waiting on their
// owner (`owner=not_joined`), across every page. The venues, stations, plan, subscription and online
// payments columns spec/14 §3.1 lists join the row with the features that own them. Reading the list
// writes no audit row: it is HQ's own record. Reading the setup column does, in each organisation it
// shows, as HQ's venue list does — it reaches into the organisation's venues.
//
// HQ has complete access (the product owner, 2026-09-22): the HQ access level is shown, never used to
// hide anything here.

import type { HqOrganization, HqOrganizationListItem } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Chip, EmptyState, InfoTip, SegmentedControl, StatCard, Table, type TableColumn } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import { problemCode, PROBLEM } from '../../api/problems';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { Page, SearchField, SummaryStrip, TableSkeleton, Toolbar, ToolbarSpring } from '../../ui';
import { TickingNumber } from '../../ui/ticking';
import { NotPermitted, RequirePlatformPermission } from '../access';
import { HQ_PATHS } from '../paths';
import { HQ_ORGANIZATIONS } from '../sections';
import { OwnerStatusBadge } from './OwnerInvitationPanel';
import { organizationParams, useOrganizationList, type OrganizationListFilter } from './queries';
import { SetupCell, useHqSetupOf } from './SetupProgress';
import '../hq.css';

export function OrganizationsPage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_ORGANIZATIONS}>
      <OrganizationList />
    </RequirePlatformPermission>
  );
}

type View = 'list' | 'tree';

function OrganizationList() {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const [filter, setFilter] = useState<OrganizationListFilter>('all');
  const list = useOrganizationList(filter);
  const [view, setView] = useState<View>('list');
  const [query, setQuery] = useState('');
  const loadedIds = useMemo(() => (list.data?.pages ?? []).flatMap((page) => page.data.map((row) => row.id)), [list.data]);
  const setup = useHqSetupOf(loadedIds);

  const columns = useMemo<readonly TableColumn<HqOrganizationListItem>[]>(
    () => [
      {
        key: 'name',
        header: t('organizations.column.name'),
        cell: (organization) => (
          <span className="console-hq-name">
            <Link to={HQ_PATHS.organization} params={organizationParams(organization.id)} className="console-hq-name__link">
              {organization.name}
            </Link>
            <span className="console-hq-secondary">{organization.slug}</span>
          </span>
        ),
      },
      {
        key: 'legalName',
        header: t('organizations.column.legalName'),
        cell: (organization) => organization.legalName ?? <span className="console-subtle">{t('organizations.page.notRecorded')}</span>,
      },
      { key: 'kind', header: t('organizations.column.kind'), cell: (organization) => <span className="console-muted">{t(`organizations.kind.${organization.kind}`)}</span> },
      {
        key: 'status',
        header: t('organizations.column.status'),
        cell: (organization) => <OrganizationStatus status={organization.status} />,
      },
      {
        key: 'owner',
        header: t('organizations.column.owner'),
        cell: (organization) => (
          <span className="console-hq-owner-cell">
            <OwnerStatusBadge status={organization.owner.status} />
            {organization.owner.at === null ? null : (
              <span className="console-hq-secondary console-num">{format.date(organization.owner.at, 'medium')}</span>
            )}
          </span>
        ),
      },
      { key: 'hqAccess', header: t('organizations.column.hqAccess'), cell: (organization) => t(`organizations.hqAccess.${organization.hqAccess}`) },
      {
        key: 'setup',
        header: t('organizations.column.setup'),
        cell: (organization) => <SetupCell setup={setup?.get(organization.id)} loading={setup === undefined} />,
      },
      {
        key: 'created',
        header: t('organizations.column.created'),
        cell: (organization) => <span className="console-hq-nowrap console-num">{format.date(organization.createdAt, 'medium')}</span>,
      },
    ],
    [t, format, setup],
  );

  const create = (
    <Button asChild variant="primary">
      <Link to={HQ_PATHS.newOrganization}>{t('organizations.create')}</Link>
    </Button>
  );

  // With no organisation yet the empty state offers Create; the header does not offer it a second time.
  const emptyList = filter === 'all' && list.data !== undefined && list.data.pages.every((page) => page.data.length === 0);
  const filterControl = (
    <div className="console-hq-chips" role="group" aria-label={t('organizations.filter.label')}>
      <Chip selected={filter === 'all'} onSelectedChange={() => setFilter('all')}>
        {t('organizations.filter.all')}
      </Chip>
      <Chip selected={filter === 'not_joined'} onSelectedChange={() => setFilter('not_joined')}>
        {t('organizations.filter.notJoined')}
      </Chip>
    </div>
  );
  let body: ReactNode;
  if (list.data === undefined) {
    // The server's refusal, where `/me/access` said otherwise: said as that, not as a failure to retry.
    if (list.isError && problemCode(list.error) === PROBLEM.forbidden) return <NotPermitted />;
    body = list.isError ? <LoadFailure error={list.error} message={t('organizations.unavailable')} onRetry={() => void list.refetch()} /> : <TableSkeleton />;
  } else {
    const rows = list.data.pages.flatMap((page) => page.data);
    const needle = query.trim().toLowerCase();
    const shown = needle === '' ? rows : rows.filter((row) => `${row.name} ${row.slug} ${row.legalName ?? ''}`.toLowerCase().includes(needle));
    const count = (predicate: (row: HqOrganizationListItem) => boolean) => rows.filter(predicate).length;
    body =
      rows.length === 0 && filter === 'not_joined' ? (
        <>
          <Toolbar>
            {filterControl}
          </Toolbar>
          <EmptyState icon={<NavIcon name="organizations" />} title={t('organizations.notJoinedEmptyTitle')} description={t('organizations.notJoinedEmpty')} />
        </>
      ) : rows.length === 0 ? (
        <EmptyState icon={<NavIcon name="organizations" />} title={t('organizations.emptyTitle')} description={t('organizations.empty')} action={create} />
      ) : (
        <>
          {/* The figures as the console's summary strip (ADR-0043); the one wanting a look carries the warn rail. */}
          <SummaryStrip label={t('organizations.figures.label')}>
            <StatCard label={t('organizations.figures.total')} value={<TickingNumber className="console-num" value={rows.length} format={String} />} hint={list.hasNextPage ? t('organizations.figures.loaded') : undefined} />
            <StatCard
              label={t('organizations.figures.brands')}
              value={<TickingNumber className="console-num" value={count((row) => row.kind === 'franchisor')} format={String} />}
              hint={t('organizations.figures.franchisees', { count: count((row) => row.kind === 'franchisee') })}
            />
            <StatCard label={t('organizations.figures.independents')} value={<TickingNumber className="console-num" value={count((row) => row.kind === 'independent')} format={String} />} />
            <StatCard
              label={t('organizations.figures.attention')}
              value={<TickingNumber className="console-num" value={count((row) => row.status !== 'active')} format={String} />}
              tone={count((row) => row.status !== 'active') > 0 ? 'warn' : 'default'}
              hint={t('organizations.figures.attentionHint')}
            />
            <StatCard
              label={t('organizations.figures.notJoined')}
              value={<TickingNumber className="console-num" value={count((row) => row.owner.status !== 'joined')} format={String} />}
              tone={count((row) => row.owner.status === 'expired') > 0 ? 'warn' : 'default'}
              hint={t('organizations.figures.notJoinedHint')}
            />
          </SummaryStrip>
          <Toolbar>
            <SearchField label={t('organizations.search')} value={query} onChange={(event) => setQuery(event.target.value)} />
            {filterControl}
            <ToolbarSpring />
            <SegmentedControl<View>
              size="sm"
              label={t('organizations.view.label')}
              value={view}
              onValueChange={setView}
              options={[
                { value: 'list', label: t('organizations.view.list'), icon: <Icon name="list" /> },
                { value: 'tree', label: t('organizations.view.tree'), icon: <Icon name="layers" /> },
              ]}
            />
          </Toolbar>
          {view === 'list' ? (
            <div className="console-table-card">
              <Table
                caption={t('organizations.caption')}
                captionHidden
                columns={columns}
                rows={shown}
                rowKey={(organization) => organization.id}
                rowTone={(organization) => (organization.status === 'active' ? undefined : 'warn')}
                empty={t('organizations.noMatch')}
              />
            </div>
          ) : (
            <FranchiseTree organizations={shown} />
          )}
          {list.hasNextPage ? (
            <Button className="console-hq-more" variant="ghost" busy={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              {t('organizations.more')}
            </Button>
          ) : null}
          {list.isFetchNextPageError ? <LoadFailure error={list.error} onRetry={() => void list.fetchNextPage()} /> : null}
        </>
      );
  }

  return (
    <Page title={t('organizations.title')} meta={<InfoTip>{t('organizations.intro')}</InfoTip>} actions={emptyList ? undefined : create}>
      {body}
    </Page>
  );
}

/** The franchise tree (ADR-0019): each brand with its franchisees under it, then the independents. */
function FranchiseTree({ organizations }: { readonly organizations: readonly HqOrganization[] }): ReactNode {
  const { t } = useTranslation('hq');
  const brands = organizations.filter((row) => row.kind === 'franchisor');
  const brandIds = new Set(brands.map((row) => row.id));
  const byBrand = new Map<string, HqOrganization[]>();
  for (const row of organizations) {
    if (row.franchisorOrganizationId !== null) byBrand.set(row.franchisorOrganizationId, [...(byBrand.get(row.franchisorOrganizationId) ?? []), row]);
  }
  // A franchisee whose brand is not loaded (or not in the search) still shows, on its own.
  const loose = organizations.filter((row) => row.kind !== 'franchisor' && (row.franchisorOrganizationId === null || !brandIds.has(row.franchisorOrganizationId)));

  return (
    <div className="console-hq-tree">
      {brands.map((brand) => (
        <section key={brand.id} className="console-hq-tree__brand" aria-label={brand.name}>
          <OrganizationNode organization={brand} />
          {(byBrand.get(brand.id) ?? []).length === 0 ? (
            <p className="console-hq-tree__none console-subtle">{t('organizations.tree.noFranchisees')}</p>
          ) : (
            <ul className="console-hq-tree__children">
              {(byBrand.get(brand.id) ?? []).map((child) => (
                <li key={child.id}>
                  <OrganizationNode organization={child} />
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}
      {loose.length > 0 ? (
        <section aria-label={t('organizations.tree.independents')}>
          <h2 className="console-hq-tree__heading">{t('organizations.tree.independents')}</h2>
          <ul className="console-hq-tree__grid">
            {loose.map((row) => (
              <li key={row.id}>
                <OrganizationNode organization={row} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function OrganizationNode({ organization }: { readonly organization: HqOrganization }): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <div className="console-hq-node">
      <span className="console-hq-node__mark" aria-hidden="true">
        <NavIcon name={organization.kind === 'franchisor' ? 'organizations' : 'venues'} />
      </span>
      <span className="console-hq-node__text">
        <Link to={HQ_PATHS.organization} params={organizationParams(organization.id)} className="console-hq-name__link">
          {organization.name}
        </Link>
        <span className="console-hq-secondary">
          {t(`organizations.kind.${organization.kind}`)} · {organization.slug}
        </span>
      </span>
      <OrganizationStatus status={organization.status} />
    </div>
  );
}

export function OrganizationStatus({ status }: { readonly status: HqOrganization['status'] }) {
  const { t } = useTranslation('hq');
  return (
    <Badge tone={status === 'active' ? 'ok' : 'warn'} dot>
      {t(`organizations.status.${status}`)}
    </Badge>
  );
}
